// Satisfactory blueprint auditor: single-page frontend.
import type { JsonAudit } from './serialize';
import { GraphView } from './graph-view';
import { icon } from './icons';
import { edgeDetails, nodeDetails } from './details';
import { HUGE, beltText, clear, effClass, flowLines, flowText, fmt, h, isGeneric, itemName, pct, flowNodes } from './util';
import { WorkerError, auditBackground, auditNow } from './auditor';
import { listItems, statusOf, type AuditResult, type ItemInfo, type Status, type StatusInfo } from './pipeline';
import type { Manifest, ManifestEntry } from './manifest';
import { getBuilding } from '../data/gamedata';

type AuditResp = AuditResult;
type ListEntry = ManifestEntry & Partial<StatusInfo>;
const STATUS_TITLE: Record<Status, string> = { error: 'bottlenecks / errors', warn: 'warnings', ok: 'no problems found', none: 'nothing to audit (no machines or belts)' };
function statusDot(e: { status?: Status; errors?: number; warnings?: number }): HTMLElement {
  const st = e.status ?? 'none';
  const parts: string[] = [];
  if (e.errors) parts.push(`${e.errors} error${e.errors > 1 ? 's' : ''}`);
  if (e.warnings) parts.push(`${e.warnings} warning${e.warnings > 1 ? 's' : ''}`);
  return h('span', { class: `dot dot-${st}`, title: parts.length ? parts.join(', ') : STATUS_TITLE[st] });
}
interface Upload { name: string; sbp: File; cfg?: File; folder: string; status?: Status; errors?: number; warnings?: number }
interface Bytes { sbp: ArrayBuffer; sbpcfg?: ArrayBuffer }

const state = {
  list: [] as ListEntry[],
  uploads: new Map<string, Upload>(),
  items: [] as ItemInfo[],
  filter: '',
  selected: null as string | null,
  overrides: new Map<string, string>(), // input label -> item class
  audit: null as AuditResp | null,
  error: null as string | null,
  loading: false,
  selectedFinding: -1,
  selectedMachine: -1,
  selectedCircuit: null as string | null, // circuit id, or 'unpowered'
  sweep: null as { done: number; total: number } | null, // background status-dot sweep progress
};
// Raw file bytes per blueprint id, kept for the session so re-audits (item overrides) don't re-fetch.
const bytesCache = new Map<string, Promise<Bytes>>();

// ---------- DOM skeleton ----------
const app = document.getElementById('app')!;
const bpList = h('div', { class: 'bp-list' });
const filterInput = h('input', { type: 'search', placeholder: 'Filter blueprints…', oninput: () => { state.filter = filterInput.value; renderSidebar(); } });
const fileInput = h('input', { type: 'file', accept: '.sbp,.sbpcfg', multiple: true, onchange: () => { if (fileInput.files) handleFiles(withPaths([...fileInput.files])); fileInput.value = ''; } });
const dirInput = h('input', { type: 'file', multiple: true, onchange: () => { if (dirInput.files) handleFiles(withPaths([...dirInput.files])); dirInput.value = ''; } });
dirInput.setAttribute('webkitdirectory', '');
dirInput.setAttribute('directory', '');
const dropzone = h(
  'div',
  { class: 'dropzone' },
  'Drop .sbp files or folders here, or ',
  h('label', { class: 'link' }, 'pick files', fileInput),
  ' / ',
  h('label', { class: 'link' }, 'pick a folder', dirInput),
);
// Where the game keeps blueprints on Windows; click to copy so it can be pasted into the folder picker.
const BLUEPRINT_DIR = '%LOCALAPPDATA%\\FactoryGame\\Saved\\SaveGames\\blueprints';
const pathHint = h(
  'button',
  { type: 'button', class: 'path-hint', title: 'Blueprint folder on Windows (one sub-folder per save game). Click to copy the path.' },
  h('span', { class: 'path-label' }, 'Blueprints live in '),
  h('code', null, BLUEPRINT_DIR),
);
pathHint.addEventListener('click', async () => {
  const done = (ok: boolean) => {
    pathHint.classList.add(ok ? 'copied' : 'copy-failed');
    pathHint.querySelector('.path-label')!.textContent = ok ? 'Copied! Paste it into the folder picker: ' : 'Could not copy, select it manually: ';
    setTimeout(() => {
      pathHint.classList.remove('copied', 'copy-failed');
      pathHint.querySelector('.path-label')!.textContent = 'Blueprints live in ';
    }, 2500);
  };
  try {
    await navigator.clipboard.writeText(BLUEPRINT_DIR);
    done(true);
  } catch {
    // Clipboard API needs a secure context; fall back to selecting the text.
    const range = document.createRange();
    range.selectNodeContents(pathHint.querySelector('code')!);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    done(document.execCommand?.('copy') ?? false);
  }
});
const sweepStatus = h('span', { class: 'sweep', title: 'Computing the status dots in the background' });
const sidebar = h(
  'aside',
  { class: 'sidebar' },
  h('div', { class: 'side-head' }, h('h1', null, 'Blueprint Auditor', sweepStatus), dropzone, pathHint, filterInput),
  bpList,
);
const topbar = h('header', { class: 'topbar' });
const summary = h('div', { class: 'summary' });
const splitter = h('div', { class: 'splitter', title: 'Drag to resize' });
const graph = new GraphView({
  nodeDetails: (n) => nodeDetails(state.audit!, n),
  edgeDetails: (e) => edgeDetails(state.audit!, e),
});
const main = h('main', { class: 'main' }, topbar, summary, splitter, graph.root);
const itemList = h('datalist', { id: 'item-dl' });
app.append(sidebar, main, itemList);

// ---------- drop zone / upload ----------
for (const ev of ['dragenter', 'dragover']) document.addEventListener(ev, (e) => { e.preventDefault(); dropzone.classList.add('over'); });
for (const ev of ['dragleave', 'drop']) document.addEventListener(ev, (e) => { e.preventDefault(); dropzone.classList.remove('over'); });
document.addEventListener('drop', (e) => {
  const dt = e.dataTransfer;
  if (!dt) return;
  // Prefer the entries API so dropped folders are walked recursively.
  const entries = [...dt.items].map((it) => (it.kind === 'file' ? it.webkitGetAsEntry?.() : null)).filter((x): x is FileSystemEntry => !!x);
  if (entries.length && entries.some((en) => en.isDirectory)) {
    void collectEntries(entries).then((files) => handleFiles(files));
  } else if (dt.files.length) handleFiles(withPaths([...dt.files]));
});

interface PathedFile { file: File; path: string } // path: folder part relative to what was dropped ('' for loose files)

function withPaths(files: File[]): PathedFile[] {
  return files.map((file) => {
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || '';
    const dir = rel.includes('/') ? rel.slice(0, rel.lastIndexOf('/')) : '';
    return { file, path: dir };
  });
}

/** Recursively read dropped directory entries into files, keeping their folder path. */
async function collectEntries(entries: FileSystemEntry[]): Promise<PathedFile[]> {
  const out: PathedFile[] = [];
  const readDir = (dir: FileSystemDirectoryEntry) =>
    new Promise<FileSystemEntry[]>((resolve) => {
      const reader = dir.createReader();
      const all: FileSystemEntry[] = [];
      const step = () => reader.readEntries((batch) => (batch.length ? (all.push(...batch), step()) : resolve(all)), () => resolve(all));
      step();
    });
  const fileOf = (en: FileSystemFileEntry) => new Promise<File | null>((resolve) => en.file(resolve, () => resolve(null)));
  const walk = async (en: FileSystemEntry, path: string) => {
    if (en.isFile) {
      const f = await fileOf(en as FileSystemFileEntry);
      if (f) out.push({ file: f, path });
    } else if (en.isDirectory) {
      const sub = path ? `${path}/${en.name}` : en.name;
      for (const child of await readDir(en as FileSystemDirectoryEntry)) await walk(child, sub);
    }
  };
  for (const en of entries) await walk(en, '');
  return out;
}

function handleFiles(files: PathedFile[]) {
  const sbps = files.filter((f) => /\.sbp$/i.test(f.file.name));
  const cfgs = files.filter((f) => /\.sbpcfg$/i.test(f.file.name));
  if (sbps.length === 0) {
    state.error = 'No .sbp file in the selection.';
    renderTop();
    return;
  }
  let firstId: string | null = null;
  const added: string[] = [];
  for (const { file: sbp, path } of sbps) {
    const base = sbp.name.replace(/\.sbp$/i, '');
    const cfg =
      cfgs.find((c) => c.path === path && c.file.name.replace(/\.sbpcfg$/i, '') === base)?.file ??
      (sbps.length === 1 && cfgs.length === 1 ? cfgs[0]!.file : undefined);
    const folder = path ? `Uploaded/${path}` : 'Uploaded';
    const id = `upload:${path ? path + '/' : ''}${base}`;
    state.uploads.set(id, { name: base, sbp, cfg, folder });
    added.push(id);
    firstId ??= id;
  }
  renderSidebar();
  // A single file opens straight away; a folder just gets listed (and audited in the
  // background for its status dots).
  if (sbps.length === 1 && firstId) select(firstId, new Map());
  else void auditInBackground(added);
}

/** Raw bytes for a blueprint id: a listed one is fetched (once), an upload is read from its File. */
function bytesOf(id: string): Promise<Bytes> {
  let p = bytesCache.get(id);
  if (p) return p;
  p = (async () => {
    const up = state.uploads.get(id);
    if (up) return { sbp: await up.sbp.arrayBuffer(), sbpcfg: await up.cfg?.arrayBuffer() };
    const e = state.list.find((x) => x.id === id);
    if (!e) throw new Error(id.startsWith('upload:') ? 'This uploaded blueprint is no longer available (uploads do not survive a reload). Please upload it again.' : `Unknown blueprint ${id}`);
    const get = async (url: string) => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`Could not fetch ${url}: HTTP ${r.status}`);
      return r.arrayBuffer();
    };
    return { sbp: await get(e.sbp), sbpcfg: e.sbpcfg ? await get(e.sbpcfg) : undefined };
  })();
  bytesCache.set(id, p);
  p.catch(() => bytesCache.delete(id));
  return p;
}

function nameOf(id: string): string {
  return state.uploads.get(id)?.name ?? state.list.find((e) => e.id === id)?.name ?? id;
}

// One background sweep at a time; the selected blueprint's audit jumps ahead of it inside the worker queue.
let sweepQueue: Promise<void> = Promise.resolve();
let sweepTotal = 0;
let sweepDone = 0;
/** Audit blueprints one at a time (lowest priority) so their status dots fill in. */
function auditInBackground(ids: string[]) {
  sweepTotal += ids.length;
  const tick = () => {
    sweepDone++;
    state.sweep = sweepDone >= sweepTotal ? null : { done: sweepDone, total: sweepTotal };
    if (!state.sweep) sweepTotal = sweepDone = 0;
    renderSweep();
  };
  state.sweep = { done: sweepDone, total: sweepTotal };
  renderSweep();
  for (const id of ids) {
    sweepQueue = sweepQueue.then(async () => {
      const target: Partial<StatusInfo> | undefined = state.uploads.get(id) ?? state.list.find((e) => e.id === id);
      if (!target || target.status) return tick();
      try {
        const b = await bytesOf(id);
        applyStatus(target, await auditBackground({ name: nameOf(id), sbp: b.sbp, sbpcfg: b.sbpcfg }));
      } catch (e) {
        // A broken worker is not a broken blueprint: leave the dot grey (it is retried on selection).
        if (!target.status && !(e instanceof WorkerError)) Object.assign(target, { status: 'error', errors: 1, warnings: 0 } satisfies StatusInfo);
      }
      tick();
      renderSidebar();
    });
  }
  return sweepQueue;
}

function renderSweep() {
  sweepStatus.textContent = state.sweep ? ` auditing ${state.sweep.done}/${state.sweep.total}` : '';
}

function applyStatus(target: Partial<StatusInfo>, a: AuditResp) {
  Object.assign(target, statusOf(a));
}

// ---------- summary resize ----------
{
  let start: { y: number; h: number } | null = null;
  splitter.addEventListener('pointerdown', (e) => {
    start = { y: e.clientY, h: summary.getBoundingClientRect().height };
    splitter.setPointerCapture(e.pointerId);
  });
  splitter.addEventListener('pointermove', (e) => {
    if (!start) return;
    const hgt = Math.max(60, start.h + (e.clientY - start.y));
    document.documentElement.style.setProperty('--summary-h', `${hgt}px`);
  });
  splitter.addEventListener('pointerup', () => (start = null));
}

// ---------- URL hash <-> selection ----------
function parseHash(): { id: string | null; overrides: Map<string, string> } {
  const p = new URLSearchParams(location.hash.replace(/^#/, ''));
  const overrides = new Map<string, string>();
  for (const s of p.getAll('in')) {
    const eq = s.indexOf('=');
    if (eq > 0) overrides.set(s.slice(0, eq), s.slice(eq + 1));
  }
  if (p.get('combine') === '0' && graph.combined) graph.setCombined(false);
  return { id: p.get('bp'), overrides };
}
function writeHash() {
  const p = new URLSearchParams();
  if (state.selected) p.set('bp', state.selected);
  for (const [k, v] of state.overrides) p.append('in', `${k}=${v}`);
  const next = '#' + p.toString();
  if (location.hash !== next) history.replaceState(null, '', next);
}
window.addEventListener('hashchange', () => {
  const { id, overrides } = parseHash();
  if (id !== state.selected || [...overrides].join() !== [...state.overrides].join()) select(id, overrides);
});

// ---------- data loading ----------
let loadGen = 0; // bumped per load(); a stale audit result is dropped

function select(id: string | null, overrides: Map<string, string>) {
  state.selected = id;
  state.overrides = overrides;
  // Reveal the group holding the selection even if it was collapsed earlier (e.g. opened from a link).
  if (id) {
    const folder = state.uploads.get(id)?.folder ?? state.list.find((e) => e.id === id)?.folder;
    if (folder && groupChoice(folder) === false) setGroupOpen(folder, true);
  }
  state.selectedFinding = -1;
  state.selectedMachine = -1;
  state.selectedCircuit = null;
  writeHash();
  renderSidebar();
  if (!id) {
    state.audit = null;
    state.error = null;
    renderAll();
    return;
  }
  void load();
}

async function load() {
  const gen = ++loadGen;
  const id = state.selected!;
  state.loading = true;
  state.error = null;
  renderTop();
  try {
    const inputs = [...state.overrides].map(([k, v]) => `${k}=${v}`);
    const b = await bytesOf(id);
    if (gen !== loadGen) return;
    // Runs in the worker, ahead of the background status sweep.
    const json = await auditNow({ name: nameOf(id), sbp: b.sbp, sbpcfg: b.sbpcfg, inputs });
    if (gen !== loadGen) return;
    state.audit = json;
    const target: Partial<StatusInfo> | undefined = state.uploads.get(id) ?? state.list.find((e) => e.id === id);
    if (target && state.overrides.size === 0 && !target.status) {
      applyStatus(target, json);
      renderSidebar();
    }
    state.error = null;
  } catch (e) {
    if (gen !== loadGen) return;
    state.audit = null;
    state.error = (e as Error).message;
  } finally {
    if (gen === loadGen) state.loading = false;
  }
  renderAll();
}

async function loadList() {
  try {
    const r = await fetch('blueprints/index.json');
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const j = (await r.json()) as Partial<Manifest> | null;
    if (!Array.isArray(j?.blueprints)) throw new Error('blueprints/index.json is not a manifest');
    state.list = j.blueprints.filter((e) => e && typeof e.id === 'string' && typeof e.sbp === 'string');
  } catch (e) {
    state.error = `Could not load blueprint list: ${(e as Error).message}`;
  }
  renderSidebar();
}
function loadItems() {
  state.items = listItems();
  clear(itemList);
  for (const it of state.items) itemList.append(h('option', { value: it.name }, it.form === 'solid' ? '' : it.form));
}

// ---------- sidebar group open/closed state (persisted) ----------
const GROUPS_KEY = 'sbp-auditor.groups';
function readGroups(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(GROUPS_KEY) ?? '{}');
  } catch {
    return {};
  }
}
function groupChoice(folder: string): boolean | undefined {
  const v = readGroups()[folder];
  return typeof v === 'boolean' ? v : undefined;
}
function setGroupOpen(folder: string, open: boolean) {
  try {
    const g = readGroups();
    g[folder] = open;
    localStorage.setItem(GROUPS_KEY, JSON.stringify(g));
  } catch {}
}

// ---------- rendering: sidebar ----------
function renderSidebar() {
  clear(bpList);
  const f = state.filter.trim().toLowerCase();
  const groups = new Map<string, { id: string; name: string; status?: Status; errors?: number; warnings?: number }[]>();
  for (const [id, up] of state.uploads) (groups.get(up.folder) ?? groups.set(up.folder, []).get(up.folder)!).push({ id, name: up.name, status: up.status, errors: up.errors, warnings: up.warnings });
  for (const e of state.list) (groups.get(e.folder) ?? groups.set(e.folder, []).get(e.folder)!).push(e);
  let shown = 0;
  for (const [folder, entries] of groups) {
    const matching = entries.filter((e) => !f || e.name.toLowerCase().includes(f));
    if (matching.length === 0) continue;
    shown += matching.length;
    // Groups start collapsed (so a published instance doesn't dump every sample on the
    // visitor); the group holding the selected blueprint, uploads, and any group with
    // filter matches are open. The user's choices are remembered.
    const containsSelected = matching.some((e) => e.id === state.selected);
    // An explicit click wins; otherwise uploads and the group holding the selection start open.
    const explicit = groupChoice(folder);
    const open = f ? true : explicit !== undefined ? explicit : folder.startsWith('Uploaded') || containsSelected;
    const errors = matching.filter((e) => e.status === 'error').length;
    const warns = matching.filter((e) => e.status === 'warn').length;
    const summary = h(
      'span',
      { class: 'group-summary' },
      errors ? h('span', { class: 'dot dot-error', title: `${errors} with errors` }) : null,
      errors ? `${errors} ` : null,
      warns ? h('span', { class: 'dot dot-warn', title: `${warns} with warnings` }) : null,
      warns ? `${warns} ` : null,
    );
    bpList.append(
      h(
        'div',
        { class: `bp-group${open ? '' : ' collapsed'}` },
        h(
          'button',
          {
            type: 'button',
            class: 'bp-group-title',
            'aria-expanded': String(open),
            onclick: () => {
              setGroupOpen(folder, !open);
              renderSidebar();
            },
          },
          h('span', { class: 'chevron' }, open ? '▾' : '▸'),
          h('span', { class: 'group-name', title: folder }, folder.includes('/') ? folder.slice(folder.lastIndexOf('/') + 1) : folder),
          h('span', { class: 'count' }, ` ${matching.length}`),
          summary,
        ),
        ...(open
          ? matching.map((e) =>
              h('button', { type: 'button', class: `bp-item${e.id === state.selected ? ' active' : ''}`, title: e.name, onclick: () => select(e.id, new Map()) }, statusDot(e), e.name),
            )
          : []),
      ),
    );
  }
  if (shown === 0) bpList.append(h('div', { class: 'bp-empty' }, state.list.length ? 'No blueprints match the filter.' : 'No blueprints found. Start the dev server with folder arguments (or build with them), or upload a file.'));
  bpList.querySelector('.bp-item.active')?.scrollIntoView({ block: 'nearest' });
}

// ---------- rendering: main ----------
function renderAll() {
  renderTop();
  renderSummary();
  graph.setAudit(state.audit);
}

function renderTop() {
  clear(topbar);
  const a = state.audit;
  if (!state.selected) {
    topbar.append(h('h2', null, 'Select a blueprint'), h('span', { class: 'muted' }, 'or drop a .sbp file on the sidebar'));
    if (state.error) topbar.append(h('div', { class: 'banner error' }, `✗ ${state.error}`));
    return;
  }
  const name = a?.name ?? state.uploads.get(state.selected)?.name ?? state.list.find((e) => e.id === state.selected)?.name ?? state.selected;
  topbar.append(h('h2', null, name));
  if (state.loading) topbar.append(h('span', { class: 'loading' }, 'auditing'));
  if (a) {
    topbar.append(h('span', { class: 'muted num' }, `${a.dimensions.x}×${a.dimensions.y}×${a.dimensions.z} foundations`));
    topbar.append(h('span', { class: 'muted num' }, `${a.nodes.length} nodes · ${a.edges.length} edges`));
    if (a.saveVersion) topbar.append(h('span', { class: 'muted small num' }, `save v${a.saveVersion}`));
    if (a.description) topbar.append(h('div', { class: 'desc' }, a.description));
    if (!a.converged) topbar.append(h('div', { class: 'banner' }, '⚠ flow solver did not fully converge; numbers are approximate'));
  }
  if (state.error) topbar.append(h('div', { class: 'banner error' }, `✗ ${state.error}`));
}

function section(title: string, count: number | null, body: Node, wide = false): HTMLElement {
  return h('div', { class: `section${wide ? ' wide' : ''}` }, h('h3', null, title, count !== null ? h('span', { class: 'count' }, String(count)) : null), body);
}

function renderSummary() {
  clear(summary);
  const a = state.audit;
  if (!a) {
    if (!state.loading && !state.error && state.selected === null) summary.append(h('div', { class: 'muted' }, 'Pick a blueprint from the list. The audit assumes every input belt is saturated and reports what each machine can actually do.'));
    return;
  }
  if (a.nodes.length === 0) {
    summary.append(h('div', { class: 'muted' }, 'This blueprint contains no machines, belts or pipes (foundations / decoration only). Nothing to audit.'));
    if (a.warnings.length) summary.append(renderWarnings(a));
    return;
  }
  const sections = h('div', { class: 'sections' });
  sections.append(section('Inputs', a.inputs.length, renderInputs(a)));
  sections.append(section('Outputs', a.outputs.length, renderOutputs(a)));
  sections.append(section('Machines', a.machines.reduce((s, m) => s + m.count, 0), renderMachines(a), true));
  sections.append(section('Power', a.power.circuits.length, renderPower(a), true));
  sections.append(section('Findings', a.findings.length, renderFindings(a), true));
  if (a.warnings.length) sections.append(section('', null, renderWarnings(a), true));
  summary.append(sections);
}

function renderInputs(a: JsonAudit): Node {
  if (a.inputs.length === 0) return h('div', { class: 'none' }, 'none');
  const head = h('tr', null, ...['label', 'item', 'belt (max)', 'uses/min', 'needs/min', 'status'].map((t, i) => h('th', { class: i === 3 || i === 4 ? 'num' : '' }, t)));
  const rows = a.inputs.map((inp) => {
    const overridden = state.overrides.has(inp.label);
    const badge = overridden
      ? h('span', { class: 'tag accent', title: 'You chose this item' }, 'override')
      : !inp.item || isGeneric(inp.item)
        ? h('span', { class: 'tag warn', title: 'Nothing downstream told us what this belt carries; pick an item to audit it' }, 'unknown')
        : h('span', { class: 'tag', title: 'Inferred from the recipes downstream' }, 'inferred');
    const picker = h('input', {
      list: 'item-dl',
      placeholder: 'item…',
      value: inp.item && !isGeneric(inp.item) ? itemName(a, inp.item) : '',
      title: 'Type an item name to override what this input carries',
      onchange: () => applyOverride(inp.label, picker.value),
    });
    const reset = overridden ? h('button', { type: 'button', class: 'reset', title: 'Remove override', onclick: () => applyOverride(inp.label, '') }, '×') : null;
    const itemIcon = icon(inp.item && !isGeneric(inp.item) ? inp.item : undefined, 20, inp.item ? itemName(a, inp.item) : '');
    let status: Node;
    const need = inp.required;
    const cap = inp.capacity;
    if (!inp.item) status = h('span', { class: 'warn' }, '? unknown item');
    else if (cap !== null && need > cap + 1e-6) status = h('span', { class: 'bad' }, `✗ belt too slow (needs ${fmt(need)}/min)`);
    else if (need > 1e-9) status = h('span', { class: 'good' }, `✓ needs ${fmt(need)}/min${cap !== null && cap - need > 1e-6 ? `, ${fmt(cap - need)}/min spare` : ''}`);
    else status = h('span', { class: 'muted' }, 'nothing downstream consumes it');
    const uses = inp.used >= HUGE ? `${fmt(need)} + rest` : inp.passThrough && inp.used > need + 1e-6 ? `${fmt(need)} + ${fmt(inp.used - need)} through` : fmt(inp.used);
    return h(
      'tr',
      { class: 'clickable', onclick: (e: Event) => { if ((e.target as HTMLElement).closest('input,button')) return; graph.setHighlight({ nodes: new Set([inp.nodeId]) }); } },
      h('td', { class: 'num' }, inp.label),
      h('td', null, h('div', { class: 'item-cell' }, itemIcon, picker, reset, badge)),
      h('td', { class: 'num' }, beltText(inp.belt, cap)),
      h('td', { class: 'num' }, uses),
      h('td', { class: 'num' }, need > 1e-9 ? fmt(need) : '—'),
      h('td', { class: 'status' }, status, inp.passThrough ? h('span', { class: 'tag', title: 'Excess on this belt is forwarded to an output' }, 'pass-through') : null),
    );
  });
  return h('table', { class: 'grid' }, h('thead', null, head), h('tbody', null, ...rows));
}

function applyOverride(label: string, text: string) {
  const t = text.trim().toLowerCase();
  const next = new Map(state.overrides);
  if (!t) next.delete(label);
  else {
    const it = state.items.find((i) => i.name.toLowerCase() === t) ?? state.items.find((i) => i.cls.toLowerCase() === t) ?? state.items.find((i) => i.name.toLowerCase().startsWith(t));
    if (!it) {
      state.error = `Unknown item "${text}"`;
      renderTop();
      return;
    }
    next.set(label, it.cls);
  }
  select(state.selected, next);
}

function renderOutputs(a: JsonAudit): Node {
  if (a.outputs.length === 0) return h('div', { class: 'none' }, 'none');
  const head = h('tr', null, ...['label', 'delivers', 'belt (max)', 'status'].map((t) => h('th', null, t)));
  const rows = a.outputs.map((o) => {
    const got = Object.values(o.flow).reduce((s, v) => s + v, 0);
    const ideal = Object.values(o.idealFlow).reduce((s, v) => s + v, 0);
    let status: Node = h('span');
    if (o.passThrough) status = h('span', { class: 'muted' }, 'pass-through: with saturated inputs; in practice whatever the inputs deliver beyond what the machines use');
    else if (ideal > got + 1e-6) status = h('span', { class: 'bad' }, `✗ could be ${flowText(a, o.idealFlow)} with faster belts`);
    else if (got > 1e-6 && o.capacity !== null && Math.abs(got - o.capacity) < 1e-6) status = h('span', { class: 'warn' }, 'belt saturated');
    else if (got > 1e-6) status = h('span', { class: 'good' }, '✓');
    const flows = Object.keys(o.flow).length ? flowNodes(a, o.flow, icon) : ['0/min'];
    return h(
      'tr',
      { class: 'clickable', onclick: () => graph.setHighlight({ nodes: new Set([o.nodeId]) }) },
      h('td', { class: 'num' }, o.label),
      h('td', { class: 'num' }, ...flows),
      h('td', { class: 'num' }, beltText(o.belt, o.capacity)),
      h('td', { class: 'status' }, status),
    );
  });
  return h('table', { class: 'grid' }, h('thead', null, head), h('tbody', null, ...rows));
}

function renderMachines(a: JsonAudit): Node {
  if (a.machines.length === 0) return h('div', { class: 'none' }, 'none');
  const head = h('tr', null, ...['#', 'building', 'recipe', 'clock', 'efficiency', 'each: in → out'].map((t, i) => h('th', { class: i === 0 || i === 3 ? 'num' : '' }, t)));
  const rows = a.machines.map((m, idx) => {
    const effs = m.efficiencies;
    const min = Math.min(...effs);
    const max = Math.max(...effs);
    const avg = effs.reduce((x, y) => x + y, 0) / effs.length;
    const effStr = max - min < 1e-6 ? pct(avg) : `${pct(min)}–${pct(max)} (avg ${pct(avg)})`;
    const n0 = a.nodes.find((n) => n.id === m.nodeIds[0]);
    const isGen = n0?.kind === 'generator';
    const rec = isGen
      ? h('span', null, `burning ${n0?.fuelName ?? '?'}${n0?.powerMW ? ` (${fmt(n0.powerMW)} MW each)` : ''}`)
      : m.recipeName
        ? h('span', null, m.recipeName)
        : h('span', { class: 'bad' }, 'NO RECIPE');
    const cls = m.paused ? 'none' : effClass(min, isGen || !!m.recipe);
    const each = Object.keys(m.demandEach).length || Object.keys(m.productionEach).length ? [...flowNodes(a, m.demandEach, icon), ' → ', ...flowNodes(a, m.productionEach, icon)] : [''];
    return h(
      'tr',
      {
        class: `clickable${state.selectedMachine === idx ? ' selected' : ''}`,
        onclick: () => {
          state.selectedMachine = idx;
          state.selectedFinding = -1;
          state.selectedCircuit = null;
          graph.setHighlight({ nodes: new Set(m.nodeIds) });
          renderSummary();
        },
      },
      h('td', { class: 'num' }, `${m.count}×`),
      h('td', null, h('span', { class: 'with-icon' }, icon(m.className, 20, m.name), m.name)),
      h('td', null, rec),
      h('td', { class: 'num' }, Math.abs(m.clock - 1) > 1e-6 ? `${fmt(m.clock * 100)}%` : ''),
      h('td', null, m.paused ? h('span', { class: 'tag', title: 'Switched off in the blueprint; treated as absent' }, 'paused') : h('span', { class: `eff-text ${cls}` }, effStr)),
      h('td', { class: 'num muted' }, ...each),
    );
  });
  return h('table', { class: 'grid' }, h('thead', null, head), h('tbody', null, ...rows));
}

function renderPower(a: JsonAudit): Node {
  const p = a.power;
  if (p.circuits.length === 0 && p.unpoweredNodeIds.length === 0) return h('div', { class: 'none' }, 'no powered buildings');
  const head = h('tr', null, ...['circuit', 'machines', 'poles', 'draw MW', 'generated MW'].map((t, i) => h('th', { class: i > 0 ? 'num' : '' }, t)));
  const pick = (id: string, nodeIds: string[]) => {
    state.selectedCircuit = id;
    state.selectedMachine = -1;
    state.selectedFinding = -1;
    graph.setHighlight({ nodes: new Set(nodeIds) });
    renderSummary();
  };
  const byId = new Map(a.nodes.map((n) => [n.id, n]));
  const nameList = (ids: string[]) => ids.map((id) => byId.get(id)?.label ?? id).join(', ');
  const rows = p.circuits.map((c) => {
    // No pole sprites in the icon sheet, so the breakdown is text: "Power Pole Mk.2 ×3, Wall Outlet ×1".
    const poleEntries = Object.entries(c.poleClasses);
    const poleCell = poleEntries.length ? h('span', { class: 'muted small' }, poleEntries.map(([cls, n]) => `${getBuilding(cls)?.name ?? cls} ×${n}`).join(', ')) : null;
    const split = p.circuits.filter((x) => x.nodeIds.length > 0).length >= 2 && c.nodeIds.length > 0;
    return h(
      'tr',
      { class: `clickable${state.selectedCircuit === c.id ? ' selected' : ''}`, title: nameList(c.nodeIds), onclick: () => pick(c.id, c.nodeIds) },
      h('td', null, h('span', { class: split ? 'warn' : '' }, c.label)),
      h('td', { class: 'num' }, c.nodeIds.length ? String(c.nodeIds.length) : h('span', { class: 'muted' }, 'poles only')),
      h('td', { class: 'num' }, String(c.poleCount), ' ', poleCell),
      h('td', { class: 'num' }, fmt(c.consumptionMW)),
      h('td', { class: 'num' }, c.productionMW > 0 ? fmt(c.productionMW) : h('span', { class: 'muted' }, '0')),
    );
  });
  if (p.unpoweredNodeIds.length) {
    rows.push(
      h(
        'tr',
        { class: `clickable${state.selectedCircuit === 'unpowered' ? ' selected' : ''}`, title: nameList(p.unpoweredNodeIds), onclick: () => pick('unpowered', p.unpoweredNodeIds) },
        h('td', null, h('span', { class: 'bad' }, '⚠ unpowered')),
        h('td', { class: 'num' }, String(p.unpoweredNodeIds.length)),
        h('td', { class: 'num muted' }, '—'),
        h('td', { class: 'num muted' }, '—'),
        h('td', { class: 'num muted' }, '—'),
      ),
    );
  }
  return h('table', { class: 'grid' }, h('thead', null, head), h('tbody', null, ...rows));
}

function renderFindings(a: JsonAudit): Node {
  if (a.findings.length === 0) return h('div', { class: 'good' }, '✓ no bottlenecks found');
  return h(
    'ul',
    { class: 'findings' },
    ...a.findings.map((f, idx) => {
      const sev = f.severity === 'error' ? ['✗', 'bad'] : f.severity === 'warn' ? ['⚠', 'warn'] : ['ℹ', 'info'];
      return h(
        'li',
        {
          class: state.selectedFinding === idx ? 'selected' : '',
          title: 'Click to highlight in the graph',
          onclick: () => {
            state.selectedFinding = idx;
            state.selectedMachine = -1;
            state.selectedCircuit = null;
            const nodes = new Set(f.nodeIds ?? []);
            if (f.edgeId) {
              const e = a.edges.find((x) => x.id === f.edgeId);
              if (e) { nodes.add(e.fromNode); nodes.add(e.toNode); }
            }
            graph.setHighlight({ nodes, edge: f.edgeId });
            renderSummary();
          },
        },
        h('span', { class: `sev ${sev[1]}` }, sev[0]),
        h('span', null, f.message),
        h('span', { class: 'kind' }, f.kind),
      );
    }),
  );
}

function renderWarnings(a: JsonAudit): Node {
  return h(
    'details',
    { class: 'warnings' },
    h('summary', null, `Parser warnings (${a.warnings.length})`),
    h('ul', null, ...a.warnings.map((w) => h('li', null, w))),
  );
}

// ---------- boot ----------
loadItems();
void loadList().then(() => {
  const { id, overrides } = parseHash();
  select(id, overrides);
  // Status dots for every listed blueprint, computed one at a time behind the selection.
  void auditInBackground(state.list.map((e) => e.id));
});
renderAll();
